import { messageStamp } from 'shared/helpers/timeHelper';
import { getDateTimeFormatPattern } from 'shared/helpers/formatTime';

const ACTIVITY_ICONS = {
  status: {
    icon: 'i-lucide-circle-dot',
    className: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300',
  },
  create: {
    icon: 'i-lucide-plus',
    className: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-300',
  },
  edit: {
    icon: 'i-lucide-pencil',
    className: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300',
  },
  add: {
    icon: 'i-lucide-calendar-plus',
    className: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-300',
  },
  remove: {
    icon: 'i-lucide-calendar-minus',
    className: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300',
  },
  assign: {
    icon: 'i-lucide-user',
    className: 'bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-300',
  },
  quote: {
    icon: 'i-lucide-file-text',
    className: 'bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-300',
  },
  quote_send: {
    icon: 'i-lucide-send',
    className: 'bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300',
  },
  quote_remind: {
    icon: 'i-lucide-bell',
    className: 'bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-300',
  },
  quote_accept: {
    icon: 'i-lucide-check',
    className: 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-300',
  },
  quote_decline: {
    icon: 'i-lucide-x',
    className: 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-300',
  },
  general: {
    icon: 'i-lucide-history',
    className: 'bg-n-slate-3 text-n-slate-11',
  },
};

export const activityIconFor = iconKey =>
  ACTIVITY_ICONS[iconKey] || ACTIVITY_ICONS.general;

export const formatAuditActor = (auditLog, labels = {}) => {
  const { agentBotLabel = 'Agent bot', systemLabel = 'System' } = labels;

  if (auditLog.username) return auditLog.username;
  if (auditLog.actor_name) return auditLog.actor_name;
  if (auditLog.user_type === 'AgentBot' || auditLog.source === 'agent_bot') {
    return agentBotLabel;
  }
  return systemLabel;
};

const activityItemsFrom = auditOrChanges => {
  if (Array.isArray(auditOrChanges)) return auditOrChanges;
  if (Array.isArray(auditOrChanges?.items)) return auditOrChanges.items;
  if (Array.isArray(auditOrChanges?.changes)) return auditOrChanges.changes;
  return [];
};

const ISO_DATE_VALUE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Format ISO date/datetime strings with the user's timezone + time format prefs.
 * Also normalize boolean true/false strings to yes/no.
 */
export const formatActivityDisplayValue = value => {
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'string') {
    if (/^true$/i.test(value)) return 'yes';
    if (/^false$/i.test(value)) return 'no';
  }

  if (typeof value !== 'string' || !ISO_DATE_VALUE_RE.test(value)) return value;

  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return value;

  return messageStamp(
    Math.floor(ms / 1000),
    getDateTimeFormatPattern('MMM dd, yyyy')
  );
};

/**
 * Normalize server activity items for UI rendering.
 */
export const formatAuditChangeEntries = (auditOrChanges, options = {}) => {
  const { includeActor = true } = options;
  const presented = activityItemsFrom(auditOrChanges);

  return presented
    .map(entry => {
      const sentence = entry.sentence || {};
      const actor = includeActor ? sentence.actor || null : null;
      const before = formatActivityDisplayValue(
        sentence.before ?? entry.before ?? null
      );
      const after = formatActivityDisplayValue(
        sentence.after ?? entry.after ?? null
      );
      const detail = formatActivityDisplayValue(
        sentence.detail ?? entry.detail ?? null
      );
      return {
        field: entry.field,
        kind: entry.kind || 'fallback',
        icon: entry.icon || 'general',
        label: entry.label,
        before,
        after,
        target: sentence.target ?? null,
        detail,
        verb: sentence.verb || '',
        actor,
        sentence: {
          actor,
          verb: sentence.verb || '',
          before,
          after,
          target: sentence.target ?? null,
          detail,
        },
      };
    })
    .filter(entry => entry.verb || entry.before || entry.after || entry.target);
};

export const buildActivitySentenceText = (entry, { includeActor = true } = {}) => {
  if (!entry) return '';
  const sentence = entry.sentence || entry;
  const parts = [];
  if (includeActor && sentence.actor) parts.push(sentence.actor);
  if (sentence.verb) parts.push(sentence.verb);
  if (sentence.target) parts.push(sentence.target);
  if (sentence.before && sentence.after && !sentence.target) {
    parts.push(sentence.before, 'to', sentence.after);
  } else if (sentence.after && sentence.verb?.includes('to')) {
    parts.push(sentence.after);
  } else if (sentence.before && sentence.after) {
    parts.push(sentence.before, 'to', sentence.after);
  } else if (sentence.after && !sentence.target) {
    parts.push(sentence.after);
  } else if (sentence.after && sentence.target) {
    parts.push('to', sentence.after);
  }
  if (sentence.detail) {
    parts.push('with reason', sentence.detail);
  }
  return parts.filter(Boolean).join(' ');
};

export const summarizeLastChange = (lastChange, labels = {}) => {
  if (!lastChange || typeof lastChange !== 'object') return null;

  const entries = formatAuditChangeEntries(lastChange, { includeActor: true });
  const first = entries[0];
  if (!first && !lastChange.action) return null;

  const actor =
    first?.actor ||
    formatAuditActor(
      {
        username: lastChange.actor_name,
        source: lastChange.source,
      },
      labels
    );

  return {
    actor,
    createdAt: lastChange.created_at,
    action: lastChange.action,
    changeLine: first ? buildActivitySentenceText(first, { includeActor: false }) : null,
    entries,
    items: entries,
  };
};
